import React, { useState } from "react";
import { toast } from "sonner";
import { X, Tag, MessageSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const STAGE_META = {
  sourced: { label: "Sourced", color: "#94a3b8" },
  contacted: { label: "Contacted", color: "#60a5fa" },
  shortlisted: { label: "Shortlisted", color: "#FBBF24" },
  interviewing: { label: "Interviewing", color: "#a78bfa" },
  offer: { label: "Offer", color: "#34d399" },
  hired: { label: "Hired", color: "#22c55e" },
  rejected: { label: "Rejected", color: "#f87171" },
};
const STAGES = Object.keys(STAGE_META);

export default function PipelineBoard({ board, onRefresh, onContact }) {
  const [tagInputFor, setTagInputFor] = useState(null);
  const [tagValue, setTagValue] = useState("");

  const moveStage = async (userId, newStage) => {
    try {
      await api.put(`/recruiter/shortlist/${userId}/stage`, { stage: newStage });
      toast.success(`Moved to ${STAGE_META[newStage].label}`);
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update stage");
    }
  };

  const saveTags = async (userId) => {
    const tags = tagValue.split(",").map((t) => t.trim()).filter(Boolean);
    try {
      await api.put(`/recruiter/shortlist/${userId}/tags`, { tags });
      setTagInputFor(null);
      setTagValue("");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save tags");
    }
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-2" data-testid="recruiter-pipeline-board">
      {STAGES.map((stage) => {
        const meta = STAGE_META[stage];
        const candidates = board[stage] || [];
        return (
          <div key={stage} className="neu-card rounded-2xl p-4 w-64 shrink-0" data-testid={`pipeline-column-${stage}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: meta.color }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} /> {meta.label}
              </span>
              <span className="text-[11px] font-mono text-slate-500">{candidates.length}</span>
            </div>
            <div className="space-y-2.5 min-h-[60px]">
              {candidates.map((c) => (
                <div key={c.user_id} className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`pipeline-card-${c.user_id}`}>
                  <p className="text-sm font-semibold text-white">{c.name}</p>
                  <p className="text-[11px] text-slate-400">{c.level_info?.title} · {c.xp} XP</p>
                  {c.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {c.tags.map((t) => (
                        <span key={t} className="text-[9px] font-semibold text-indigo-300 bg-indigo-400/10 rounded-full px-1.5 py-0.5">{t}</span>
                      ))}
                    </div>
                  )}
                  {tagInputFor === c.user_id ? (
                    <div className="flex items-center gap-1 mt-2">
                      <input
                        autoFocus value={tagValue} onChange={(e) => setTagValue(e.target.value)}
                        placeholder="tag1, tag2" data-testid={`pipeline-tag-input-${c.user_id}`}
                        className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] text-white focus:outline-none"
                        onKeyDown={(e) => e.key === "Enter" && saveTags(c.user_id)}
                      />
                      <button onClick={() => saveTags(c.user_id)} data-testid={`pipeline-tag-save-${c.user_id}`} className="text-[10px] text-emerald-400 font-semibold">Save</button>
                      <button onClick={() => setTagInputFor(null)} className="text-slate-500"><X className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setTagInputFor(c.user_id); setTagValue((c.tags || []).join(", ")); }}
                      data-testid={`pipeline-add-tag-btn-${c.user_id}`}
                      className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-indigo-300 mt-2"
                    >
                      <Tag className="w-2.5 h-2.5" /> Edit tags
                    </button>
                  )}
                  <div className="flex items-center justify-between mt-2.5 gap-1.5">
                    <select
                      value={stage} onChange={(e) => moveStage(c.user_id, e.target.value)}
                      data-testid={`pipeline-stage-select-${c.user_id}`}
                      className="flex-1 bg-white/5 border border-white/10 rounded px-1.5 py-1 text-[10px] text-white focus:outline-none"
                    >
                      {STAGES.map((s) => <option key={s} value={s} className="bg-[#0f1522]">{STAGE_META[s].label}</option>)}
                    </select>
                    <button onClick={() => onContact(c)} data-testid={`pipeline-contact-btn-${c.user_id}`} className="text-slate-400 hover:text-indigo-300 shrink-0"><MessageSquare className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              {candidates.length === 0 && <p className="text-[11px] text-slate-500 text-center py-4">Empty</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
